import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useWebSocket } from '../context/WebSocketContext';
import { settingsApi } from "../api/settings";
import payApi from '../api/pay';
import Swal from 'sweetalert2';
import DepositSuccessPopup from '../components/Popup/DepositSuccessPopup';

// Import Gateway Logos
import GCashLogo from "../assets/icons/popup/GCASH-Logo.svg?react";
import MayaLogo from "../assets/icons/popup/MAYA-Logo.svg?react";
import QRPHLogo from "../assets/icons/popup/QR_Ph_Logo.svg?react";
import QRPHLogoPlus from "../assets/icons/popup/QR_Ph_Logo_Plus.svg?react";
import GCashPlusLogo from "../assets/icons/popup/gcash-plus-large.svg?react";
import PayWalletLogo from "../assets/icons/popup/paywallet-logo.svg?react";
import PayWalletXLogo from "../assets/icons/popup/paywalletx-logo.svg?react";

const PHMPAY_GATEWAYS = {
  phmpay_qrph: {
    payMethod: 'PH_QRPH_DYNAMIC',
    fallbackMinAmountKey: 'phmpay',
  },
  phmpay_gcashplus: {
    payMethod: 'PH_GCASH_URL',
    fallbackMinAmountKey: 'phmpay',
  },
};

const getGatewayMinAmount = (minAmounts, gatewayKey, defaultMinAmount) => {
  if (minAmounts?.[gatewayKey]) {
    return minAmounts[gatewayKey];
  }

  const fallbackMinAmountKey = PHMPAY_GATEWAYS[gatewayKey]?.fallbackMinAmountKey;
  if (fallbackMinAmountKey && minAmounts?.[fallbackMinAmountKey]) {
    return minAmounts[fallbackMinAmountKey];
  }

  if (gatewayKey === 'paywalletx' && minAmounts?.paywallet) {
    return minAmounts.paywallet;
  }

  if (gatewayKey === 'paywallet_gcash' && minAmounts?.paywallet) {
    return minAmounts.paywallet;
  }

  return defaultMinAmount;
};

const normalizeGatewayKeys = (gatewayKeys = []) => {
  const normalized = gatewayKeys.flatMap((gateway) => {
    if (gateway === 'phmpay') {
      return ['phmpay_qrph'];
    }

    return gateway;
  });

  return [...new Set(normalized)];
};

const PAYMENT_METHODS_CONFIG = {
  gcash: {
    label: "GCash",
    Logo: GCashLogo,
    apiCallKey: 'pay',
    defaultMinAmount: 50,
    disabled: true, // Set to true to manually disable this payment method
  },
  maya: {
    label: "Maya",
    Logo: MayaLogo,
    apiCallKey: 'payMaya',
    defaultMinAmount: 50,
    disabled: false, // Set to true to manually disable this payment method
  },
  qrph: {
    label: "QRPH",
    Logo: QRPHLogo,
    apiCallKey: 'pay',
    defaultMinAmount: 2000,
    disabled: true, // Set to true to manually disable this payment method
  },
  phmpay_qrph: {
    label: "QRPH",
    Logo: QRPHLogo,
    apiCallKey: 'payPhmpay',
    defaultMinAmount: 50,
    disabled: false, // Set to false to enable Philippines wallet payments
  },
  phmpay_gcashplus: {
    label: "GCash+",
    Logo: GCashPlusLogo,
    apiCallKey: 'payPhmpay',
    defaultMinAmount: 50,
    disabled: false,
  },
  paywallet: {
    label: "QRPH-1",
    Logo: PayWalletLogo,
    apiCallKey: 'payPaywallet',
    defaultMinAmount: 50,
    disabled: false,
    accentClass: 'border-teal-400 bg-gradient-to-r from-teal-50 to-emerald-50',
  },
  paywalletx: {
    label: "QRPH-2",
    Logo: PayWalletXLogo,
    apiCallKey: 'payPaywallet',
    defaultMinAmount: 50,
    disabled: false,
    accentClass: 'border-violet-400 bg-gradient-to-r from-violet-50 to-purple-50',
  },
  paywallet_gcash: {
    label: "GCASH-NOW",
    Logo: GCashPlusLogo,
    apiCallKey: 'payPaywallet',
    defaultMinAmount: 50,
    disabled: false,
    accentClass: 'border-sky-400 bg-gradient-to-r from-sky-50 to-blue-50',
  },
};

export default function DepositPage() {
  const [amount, setAmount] = useState(50);
  const [isLoading, setIsLoading] = useState(false);
  const [availableGateways, setAvailableGateways] = useState([]);
  const [gatewayMinAmounts, setGatewayMinAmounts] = useState({});
  const [isLoadingGateways, setIsLoadingGateways] = useState(false);
  const [selectedGateway, setSelectedGateway] = useState(null);
  const [currentBalance, setCurrentBalance] = useState(0);
  const [showSuccess, setShowSuccess] = useState(false);
  const [paymentSuccessData, setPaymentSuccessData] = useState([]);
  
  const { user, tokens } = useAuth();
  const { resetPaymentStatus, refreshWalletBalance } = useWebSocket();

  const getCurrentMinAmount = () => {
    if (selectedGateway) {
      return gatewayMinAmounts[selectedGateway] || PAYMENT_METHODS_CONFIG[selectedGateway]?.defaultMinAmount || 50;
    }
    return 50;
  };

  useEffect(() => {
    const fetchGateways = async () => {
      if (tokens?.access?.token && user?.id) {
        setIsLoadingGateways(true);
        try {
          const response = await settingsApi.getSettingsByKey(tokens.access.token, 'activePaymentGateway');
          const otherSettings = response.data?.otherSettings || response.data?.other_settings;
          const activeGatewaysList = normalizeGatewayKeys(otherSettings?.gateways || []);
          const supportedGateways = activeGatewaysList.filter((gateway) => {
            const config = PAYMENT_METHODS_CONFIG[gateway];
            return config && !config.disabled;
          });

          const fallbackGateways = Object.keys(PAYMENT_METHODS_CONFIG).filter((gateway) => {
            const config = PAYMENT_METHODS_CONFIG[gateway];
            return config && !config.disabled;
          });

          const resolvedGateways = supportedGateways.length > 0 ? supportedGateways : fallbackGateways;
          
          setAvailableGateways(resolvedGateways);
          
          const minAmounts = {};
          resolvedGateways.forEach(gateway => {
            minAmounts[gateway] = getGatewayMinAmount(
              otherSettings?.minAmounts,
              gateway,
              PAYMENT_METHODS_CONFIG[gateway]?.defaultMinAmount || 50
            );
          });
          setGatewayMinAmounts(minAmounts);
          
          // Auto-select first gateway if only one available
          if (resolvedGateways.length === 1) {
            setSelectedGateway(resolvedGateways[0]);
          }
          
          if (resolvedGateways.length === 0) {
            Swal.fire('Configuration Issue', 'All payment methods are currently disabled in the configuration.', 'warning');
          }
        } catch (error) {
          console.error("Failed to fetch payment gateways:", error);
          const supportedGateways = Object.keys(PAYMENT_METHODS_CONFIG).filter(gw => {
            const config = PAYMENT_METHODS_CONFIG[gw];
            return config && !config.disabled;
          });
          setAvailableGateways(supportedGateways);
          
          const minAmounts = {};
          supportedGateways.forEach(gw => {
            minAmounts[gw] = PAYMENT_METHODS_CONFIG[gw]?.defaultMinAmount || 50;
          });
          setGatewayMinAmounts(minAmounts);
        } finally {
          setIsLoadingGateways(false);
        }
      }
    };

    fetchGateways();
    resetPaymentStatus();
  }, [tokens, user, resetPaymentStatus]);

  useEffect(() => {
    if (selectedGateway) {
      // Set default amount to 100 when gateway is selected (instead of minimum amount)
      setAmount(100);
    }
  }, [selectedGateway]);

  const handleDeposit = async () => {
    if (!selectedGateway) {
      Swal.fire('Error', 'Please select a payment gateway.', 'error');
      return;
    }

    const numericAmount = parseFloat(amount);
    
    if (isNaN(numericAmount) || numericAmount <= 0) {
      Swal.fire({
        title: 'Invalid Amount',
        text: 'Please enter a valid amount greater than 0.',
        icon: 'error',
      });
      return;
    }

    setIsLoading(true);
    let data;
    
    // Prepare data based on gateway type
    if (PHMPAY_GATEWAYS[selectedGateway]) {
      // Philippines wallet requires specific fields
      data = {
        amount: amount,
        currency: 'PHP',
        name: user.name,
        email: user.email,
        payMethod: PHMPAY_GATEWAYS[selectedGateway].payMethod,
        gateway: selectedGateway,
      };
    } else if (selectedGateway === 'paywallet') {
      data = {
        amount: amount,
        currency: 'PHP',
        name: user.name,
        email: user.email,
        paymentMethod: 'QRPH',
      };
    } else if (selectedGateway === 'paywalletx') {
      data = {
        amount: amount,
        currency: 'PHP',
        name: user.name,
        email: user.email,
        paymentMethod: 'PH_QRPH_DYNAMIC_2',
      };
    } else if (selectedGateway === 'paywallet_gcash') {
      data = {
        amount: amount,
        currency: 'PHP',
        name: user.name,
        email: user.email,
        paymentMethod: 'PH_GCASH_URL',
      };
    } else {
      // Standard data for other gateways
      data = {
        amount: amount,
        currency: 'PHP',
        name: user.name,
        email: user.email,
        gateway: selectedGateway,
      };
    }

    try {
      let response;
      const methodConfig = PAYMENT_METHODS_CONFIG[selectedGateway];

      if (!methodConfig) {
        throw new Error('Invalid payment method configuration.');
      }

      if (methodConfig.apiCallKey === 'payMaya') {
        response = await payApi.payMaya(tokens.access.token, data);
      } else if (methodConfig.apiCallKey === 'payPhmpay') {
        response = await payApi.payPhmpay(tokens.access.token, data);
      } else if (methodConfig.apiCallKey === 'payPaywallet') {
        response = await payApi.payPaywallet(tokens.access.token, data);
      } else if (methodConfig.apiCallKey === 'pay') {
        response = await payApi.pay(tokens.access.token, data);
      } else {
        throw new Error(`No API call defined for ${selectedGateway}`);
      }
      
      if (response.status === 200) {
        console.log('API Response:', response.data);
        console.log('Selected Gateway:', selectedGateway);
        
        // Handle Maya-specific response format
        if (selectedGateway === 'maya') {
          // For Maya, the response includes redirectUrl, paymentId, and qrCode
          const mayaData = {
            ...response.data,
            gateway: 'maya',
            amount: amount,
            currency: 'PHP'
          };
          console.log('Maya Data being set:', mayaData);
          setPaymentSuccessData(mayaData);
          
        } else if (PHMPAY_GATEWAYS[selectedGateway]) {
          // For Philippines wallet, the response includes merchantOrderId, qrcodeRaw, and payUrl
          const phmpayData = {
            ...response.data,
            gateway: selectedGateway,
            amount: amount,
            currency: 'PHP'
          };
          console.log('Philippines wallet Data being set:', phmpayData);
          setPaymentSuccessData(phmpayData);

        } else if (selectedGateway === 'paywallet') {
          const paywalletData = {
            ...response.data,
            gateway: 'paywallet',
            amount: amount,
            currency: 'PHP'
          };
          console.log('PayWallet Data being set:', paywalletData);
          setPaymentSuccessData(paywalletData);

        } else if (selectedGateway === 'paywalletx') {
          const paywalletxData = {
            ...response.data,
            gateway: 'paywalletx',
            amount: amount,
            currency: 'PHP'
          };
          console.log('PayWalletX Data being set:', paywalletxData);
          setPaymentSuccessData(paywalletxData);

        } else if (selectedGateway === 'paywallet_gcash') {
          const paywalletGcashData = {
            ...response.data,
            gateway: 'paywallet_gcash',
            amount: amount,
            currency: 'PHP'
          };
          console.log('PayWallet GCash Data being set:', paywalletGcashData);
          setPaymentSuccessData(paywalletGcashData);
          
        } else {
          // For other gateways, use the original response data
          setPaymentSuccessData(response.data);
        }
        setShowSuccess(true);
      } else {
        Swal.fire('Payment Failed', response.data?.message || 'The payment could not be processed.', 'error');
      }
    } catch (error) {
      console.error("Payment API call error:", error);
      Swal.fire('Payment Error', error.message || 'An unexpected error occurred during payment.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setShowSuccess(false);
    // Reset form to default amount
    setAmount(100);
    refreshWalletBalance();
  };

  // Show QR code popup if payment was successful
  if (showSuccess) {
    return (
      <DepositSuccessPopup
        amount={amount}
        onClose={handleClose}
        paymentSuccessData={paymentSuccessData}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <div className="max-w-md mx-auto bg-white rounded-lg shadow-md p-6">
        <h1 className="text-2xl font-bold text-gray-800 mb-6 text-center">PAY USING EWALLET</h1>

        {/* Payment Method Selection */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Select Payment Method
          </label>
          
          {isLoadingGateways ? (
            <div className="flex justify-center items-center h-20">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : availableGateways.length > 0 ? (
            <div className="grid grid-cols-1 gap-3">
              {availableGateways.map((key) => {
                const config = PAYMENT_METHODS_CONFIG[key];
                if (!config) return null;
                const { Logo, label } = config;
                const minAmount = gatewayMinAmounts[key] || config.defaultMinAmount || 50;
                
                const accentClass = config.accentClass;
                const isSelected = selectedGateway === key;

                return (
                  <button
                    key={key}
                    onClick={() => setSelectedGateway(key)}
                    className={`flex items-center p-4 border-2 rounded-xl transition-all ${
                      isSelected
                        ? accentClass || 'border-blue-500 bg-blue-50'
                        : accentClass
                          ? `${accentClass} opacity-80 hover:opacity-100`
                          : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Logo className="w-8 h-8 mr-3" />
                    <div className="flex-1 text-left">
                      <p className="font-medium text-gray-800">{label}</p>
                      <p className="text-sm text-gray-500">Available</p>
                    </div>
                    {selectedGateway === key && (
                      <div className="w-4 h-4 bg-blue-500 rounded-full"></div>
                    )}
                  </button>
                );
              })}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-4">No payment methods available.</p>
          )}
        </div>

        {/* Amount Input */}
        {selectedGateway && (
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Amount
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">₱</span>
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter amount"
                min="1"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Enter any amount you wish to deposit
            </p>
          </div>
        )}

        {/* Deposit Button */}
        <button
          onClick={handleDeposit}
          disabled={!selectedGateway || isLoading}
          className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${
            !selectedGateway || isLoading
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-blue-600 text-white hover:bg-blue-700'
          }`}
        >
          {isLoading ? (
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
              Processing...
            </div>
          ) : (
            'Pay Now'
          )}
        </button>
      </div>
    </div>
  );
}
